import { DatePipe } from "@angular/common";
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { debounceTime, filter, Observable, Subscription } from "rxjs";
import { CALL_ACCESS_LOG_DEFAULT_SORT_COLUMN, CALL_ACCESS_LOG_DEFAULT_SORT_COLUMN_DIRECTION, CALL_ACCESS_LOG_PAGE_DEFAULT_SIZE, CALL_ACCESS_LOG_PAGE_SIZE_OPTIONS } from "src/app/conf/call-access-log";
import { ItemsPagination } from "src/app/model/api/data-rest";
import { PaginationParams } from "src/app/model/api/pagination-params";
import {
	MtmaDiagnosticsEventType,
	MtmaDiagnosticsLog,
	MTMA_DIAGNOSTICS_ERROR_SUBTYPE_VALUES,
	MTMA_DIAGNOSTICS_INFO_SUBTYPE_VALUES,
} from "src/app/model/mtma-diagnostics-log";
import { Device, MtmaIndexTab } from "src/app/model/device";
import { DeviceModel } from "src/app/model/device-model";
import { DeviceStatus } from "src/app/model/device-status";
import { MtmaModule, MtmaModuleConfig } from "src/app/model/mtma-module";
import { StandardDesignParameters, StandardDynamicComponent, StandardPageLayout } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";
import { AppState } from "src/app/store/app.reducer";
import { selecMtmaModules, selectCurrentDevice, selectCurrentDeviceModel, selectCurrentDeviceStatus, selectMtmaDiagnosticsLog, selectMtmaDiagnosticsLogAction, selectMtmaDiagnosticsLogPagination } from "src/app/store/device/device.selector";
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaModuleType } from "src/app/conf/mtma-module";
import { selectDeviceTabIndex } from "src/app/store/home/home.selector";
import { Table, TableLazyLoadEvent } from "primeng/table";
import { pageSortChange } from "src/app/services/table";
import moment from "moment";
import {
	getMtmaDiagnosticsSubTypeKey,
	getMtmaDiagnosticsValueDescriptor,
	ValueDisplayDescriptor,
} from "./mtma-diagnostics-log-display";
import { CommonCustomConfirmDialogComponent } from "src/app/modules/shared/components/common-custom-confirm-dialog/common-custom-confirm.dialog.component";
import { DialogService } from "primeng/dynamicdialog";

@Component({
	selector: 'app-mtma-diagnostics-log',
	templateUrl: './mtma-diagnostics-log.component.html',
	providers: []
})
export class MtmaDiagnosticsLogComponent implements OnInit, OnDestroy {
	@ViewChild(Table)
	table: Table;
	// saving = false;

	diagnosticsLogEnabled = false;
	online = false;
	configured = false;

	mtmaModules$: Observable<MtmaModule[] | undefined>;
	mtmaModulesSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;

	readonly MtmaDiagnosticsEventType = MtmaDiagnosticsEventType;

	readonly eventTypeOptions: { value: number | null; label: string }[] = [
		{ value: null, label: 'MTMA_DIAG_FILTER_ALL_TYPES' },
		{ value: MtmaDiagnosticsEventType.Error, label: 'MTMA_CONF_{Error}' },
		{ value: MtmaDiagnosticsEventType.Info, label: 'MTMA_CONF_{Information}' },
	];

	eventSubTypeOptions: { value: number | null; label: string }[] = [];

	// selectors
	mtmaDiagnosticsLog$: Observable<MtmaDiagnosticsLog[] | undefined>;
	mtmaDiagnosticsLogSub: Subscription | undefined;

	mtmaDiagnosticsLogPagination$: Observable<ItemsPagination | undefined>;
	mtmaDiagnosticsLogPaginationSub: Subscription | undefined;

	mtmaDiagnosticsLogAction: boolean;
	mtmaDiagnosticsLogAction$: Observable<boolean | undefined>;
	mtmaDiagnosticsLogActionSub: Subscription | undefined;

	deviceModel$: Observable<DeviceModel | undefined>;
	deviceModelSub: Subscription | undefined;

	currDevice: Device;
	currDevice$: Observable<Device | undefined>;
	currDeviceSub: Subscription | undefined;

	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;

	logs: MtmaDiagnosticsLog[] | undefined;

	logsLoading = false;

	deviceModel: DeviceModel = new DeviceModel();

	filter: '';
	filterGroup: FormGroup;
	filterSub: Subscription;
	eventTypeFilterSub!: Subscription;

	paging: PaginationParams = {
		size: CALL_ACCESS_LOG_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: CALL_ACCESS_LOG_DEFAULT_SORT_COLUMN,
		order: CALL_ACCESS_LOG_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSize: number = CALL_ACCESS_LOG_PAGE_DEFAULT_SIZE;
	pageSizeOptions = CALL_ACCESS_LOG_PAGE_SIZE_OPTIONS;
	resultLength: number;

	dateTransform: DatePipe;

	minDate: Date = new Date("1900-01-01");
	today = new Date();
	maxDate = new Date();

	screenWidth: number;
	constructor(
		private store: Store<AppState>,
		private formBuilder: FormBuilder,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}
	ngOnInit(): void {
		this.today = new Date();
		this.maxDate = new Date();
		this.maxDate.setHours(23);
		this.maxDate.setMinutes(59);

		const endDate = new Date(this.today.getTime());
		const startDate = new Date(this.today.getTime());
		startDate.setDate(startDate.getDate() - 7);
		const curreLang = this.translate.currentLang;
		this.dateTransform = new DatePipe(curreLang);
		this.filterGroup = new FormGroup({
			'startDateTime': new FormControl(startDate, Validators.required),
			'endDateTime': new FormControl(endDate, Validators.required),
			'eventType': new FormControl<number | null>(null),
			'eventSubType': new FormControl<number | null>({ value: null, disabled: true }),
		});

		this.rebuildEventSubTypeOptions(null);
		this.eventTypeFilterSub = this.f.eventType.valueChanges.subscribe((t: number | null) => {
			this.f.eventSubType.setValue(null, { emitEvent: false });
			this.rebuildEventSubTypeOptions(t);
			if (t == null) {
				this.f.eventSubType.disable();
			} else {
				this.f.eventSubType.enable();
			}
		});

		this.subscribeAll();
		this.onResize();
	}

	ngOnDestroy(): void {
		this.mtmaDiagnosticsLogSub?.unsubscribe();
		this.mtmaDiagnosticsLogPaginationSub?.unsubscribe();
		this.currDeviceSub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
		this.filterSub?.unsubscribe();
		this.eventTypeFilterSub?.unsubscribe();
		this.deviceModelSub?.unsubscribe();
		this.mtmaModulesSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.mtmaDiagnosticsLogActionSub?.unsubscribe();
	}

	initSelectors(): void {
		this.mtmaDiagnosticsLog$ = this.store.select(selectMtmaDiagnosticsLog);
		this.mtmaDiagnosticsLogPagination$ = this.store.select(selectMtmaDiagnosticsLogPagination);
		this.currDevice$ = this.store.select(selectCurrentDevice);
		this.tabIndex$ = this.store.select(selectDeviceTabIndex);
		this.deviceModel$ = this.store.select(selectCurrentDeviceModel);
		this.mtmaModules$ = this.store.select(selecMtmaModules);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.mtmaDiagnosticsLogAction$ = this.store.select(selectMtmaDiagnosticsLogAction);
	}

	subscribeAll(): void {
		this.deviceStatusSub = this.deviceStatus$.subscribe((deviceState) => {
			if (deviceState) {
				this.configured = deviceState.Configured === 0;
				this.online = deviceState.Online;
			}
		});
		this.mtmaModulesSub = this.mtmaModules$.subscribe((mtmaModules) => {
			const module = mtmaModules?.filter((item) => item.Type === MtmaModuleType.MTMAC)[0];
			if (module !== undefined && module.Config !== undefined && module.Config.EnableLogSystem !== undefined) {
				this.diagnosticsLogEnabled = module.Config.EnableLogSystem === 1;
			} else {
				this.diagnosticsLogEnabled = false;
			}
		});
		this.mtmaDiagnosticsLogSub = this.mtmaDiagnosticsLog$.subscribe(logs => {
			this.logs = logs;
			this.logsLoading = false;
		});
		this.mtmaDiagnosticsLogPaginationSub = this.mtmaDiagnosticsLogPagination$.subscribe(pagination => {
			if (pagination !== undefined) {
				this.resultLength = pagination.TotalCount;
				this.pageSize = pagination.PageSize;
			}
		});
		this.filterSub = this.filterGroup.valueChanges.pipe(
			debounceTime(500),
		).subscribe(() => {
			this.table?.reset();
			this.dispatchGetLogs();
		});

		this.currDeviceSub = this.currDevice$.subscribe(
			(device) => {
				this.currDevice = device
				this.dispatchGetLogs();
			});
		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				this.dispatchGetLogs();
			});
		this.deviceModelSub = this.deviceModel$.pipe(filter(dm => !!dm))
			.subscribe(deviceModel => {
				this.deviceModel = deviceModel;
				this.dispatchGetLogs();
			});
		this.mtmaDiagnosticsLogActionSub = this.mtmaDiagnosticsLogAction$.subscribe(
			(mtmaDiagnosticsLogAction) => {
				if (mtmaDiagnosticsLogAction) {
					this.mtmaDiagnosticsLogAction = true;
				} else {
					setTimeout(() => {
						this.refresh();
					}, 4000);
				}
			}
		)
	}

	get f(): any {
		return this.filterGroup.controls;
	}

	/**
	 * Rebuild the event sub type options based on the event type.
	 * @param t - The event type.
	 */
	private rebuildEventSubTypeOptions(t: number | null): void {
		const allOpt = { value: null as number | null, label: 'MTMA_DIAG_FILTER_ALL_SUBTYPES' };
		if (t === MtmaDiagnosticsEventType.Error) {
			this.eventSubTypeOptions = [
				allOpt,
				...MTMA_DIAGNOSTICS_ERROR_SUBTYPE_VALUES.map((n) => ({ value: n, label: `MTMA_DIAG_ERR_ST_${n}` })),
			];
			return;
		}
		if (t === MtmaDiagnosticsEventType.Info) {
			this.eventSubTypeOptions = [
				allOpt,
				...MTMA_DIAGNOSTICS_INFO_SUBTYPE_VALUES.map((n) => ({ value: n, label: `MTMA_DIAG_INFO_ST_${n}` })),
			];
			return;
		}
		this.eventSubTypeOptions = [allOpt];
	}

	refresh(): void {
		this.today = new Date();
		this.maxDate = new Date();
		this.maxDate.setHours(23);
		this.maxDate.setMinutes(59);
		const endDate = new Date(this.today.getTime());
		this.f.endDateTime.setValue(endDate);
		this.mtmaDiagnosticsLogAction = false;
	}

	diagnosticsStateRefresh(): void {
		this.store.dispatch(DeviceActions.postMtmaDiagnosticsState({ deviceId: this.currDevice.Id }));
		this.dispatchGetLogs();
	}

	enableDiagnosticsLog(): void {
		const childRefCallsLogEnable = this.dialogService.open(CommonCustomConfirmDialogComponent, {
			header: this.translate.instant('MTMA_CONF_{Information}') + ': ' + this.translate.instant('MTMA_CONF_EnableLogDiagnostics'),
			contentStyle: { overflow: 'auto' },
			data: {
				icon: 'cm-alert',
				row1: 'DIAGNOSTICS_LOG_MOBILE_DATA_ALERT',
			}
		});
		childRefCallsLogEnable.onClose.subscribe((accepted: boolean) => {
			if (accepted) {
				const module = new MtmaModuleConfig();
				module.EnableLogSystem = 1;
				this.store.dispatch(DeviceActions.editParamMtmaModule({ module: module }));
			}
		});
	}

	isSelectLogTab(): boolean {
		return this.tabIndex === MtmaIndexTab.MTM_DIAGNOSTIC;
	}

	dispatchGetLogs(): void {
		if (this.isSelectLogTab()) {
			this.logsLoading = true;
			const startDateTime = this.f.startDateTime.value;
			const endDateTime = this.f.endDateTime.value;
			if (this.currDevice !== undefined && startDateTime !== undefined && endDateTime !== undefined) {
				const raw = this.filterGroup.getRawValue();
				const eventType = raw.eventType as number | null | undefined;
				const eventSubType = raw.eventSubType as number | null | undefined;
				this.store.dispatch(DeviceActions.getMtmaDiagnosticsLog({
					deviceId: this.currDevice.Id,
					startDate: startDateTime,
					endDate: endDateTime,
					sort: this.paging.sort,
					order: this.paging.order,
					page: this.paging.page,
					size: this.paging.size,
					...(eventType != null ? { eventType } : {}),
					...(eventType != null && eventSubType != null ? { eventSubType } : {}),
				}));
			}
		}
	}

	loadLogs(event: TableLazyLoadEvent) {
		this.paging = pageSortChange(this.paging, event);
		this.dispatchGetLogs();
	}

	getDate(date: string): Date {
		const dateChange = moment.utc(date)
		return dateChange.toDate();
	}

	displaySubType(row: MtmaDiagnosticsLog): string {
		const key = getMtmaDiagnosticsSubTypeKey(row.Type, row.SubType);
		return key ? this.translate.instant(key) : String(row.SubType);
	}

	displayValue(row: MtmaDiagnosticsLog): string {
		const descriptor = getMtmaDiagnosticsValueDescriptor(row.Type, row.SubType, row.Value);
		return this.resolveValueDisplayDescriptor(descriptor);
	}

	private resolveValueDisplayDescriptor(descriptor: ValueDisplayDescriptor): string {
		switch (descriptor.kind) {
			case 'empty':
				return '';
			case 'raw':
				return descriptor.text;
			case 'i18n':
				return this.translate.instant(descriptor.key, descriptor.params);
			default:
				return '';
		}
	}

	/**
	 * Parse the diagnostics value JSON string into an object.
	 * @param value - The JSON string to parse.
	 * @returns The parsed object or null if the JSON string is invalid.
	 */
	parseDiagnosticsValueJson(value: string | undefined | null): Record<string, unknown> | null {
		if (value === undefined || value === null || value === '') {
			return null;
		}
		try {
			let parsed: unknown = JSON.parse(value);
			if (typeof parsed === 'string' && parsed.trim().startsWith('{')) {
				try {
					parsed = JSON.parse(parsed);
				} catch {
					return null;
				}
			}
			if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
				return parsed as Record<string, unknown>;
			}
			return null;
		} catch {
			// Invalid JSON string, return null.
			return null;
		}
	}

	/**
	 * Get the diagnostics value table data for a given row.
	 * It is returned as an array of objects with the keys of the parsed JSON object.
	 * This is required since the p-table component expects an array of objects.
	 * @param row - The diagnostics log row.
	 * @returns The diagnostics value table data.
	 */
	getDiagnosticsValueTableData(row: MtmaDiagnosticsLog): Record<string, unknown>[] {
		const parsed = this.parseDiagnosticsValueJson(row?.Value);
		return parsed ? [parsed] : [];
	}

	formatDiagnosticsJsonValue(value: unknown): string {
		if (value === null || value === undefined) {
			return '';
		}
		if (typeof value === 'object') {
			return JSON.stringify(value);
		}
		return String(value);
	}

	/** Signal level text; appends % only for a finite numeric value (number or numeric string). */
	formatDiagnosticsSignalLevel(value: unknown): string {
		const text = this.formatDiagnosticsJsonValue(value);
		if (text === '') {
			return '';
		}
		if (typeof value === 'number' && Number.isFinite(value)) {
			return `${text}%`;
		}
		if (typeof value === 'string') {
			const trimmed = value.trim();
			if (trimmed !== '' && Number.isFinite(Number(trimmed))) {
				return `${text}%`;
			}
		}
		return text;
	}

	private diagnosticsCoerceInt(value: unknown): number | null {
		if (value === null || value === undefined || value === '') {
			return null;
		}
		if (typeof value === 'number' && Number.isFinite(value)) {
			return Math.trunc(value);
		}
		if (typeof value === 'string') {
			const trimmed = value.trim();
			if (trimmed === '') {
				return null;
			}
			const n = Number.parseInt(trimmed, 10);
			if (!Number.isNaN(n)) {
				return n;
			}
		}
		return null;
	}

	/**
	 * SimPinEn, CcEn, CallEventEn, AccessEventEn, SystemLogEn: 0 = disabled, 1 = enabled.
	 */
	diagnosticsBinaryTagKind(value: unknown): 'off' | 'on' | 'other' {
		const n = this.diagnosticsCoerceInt(value);
		if (n === 0) {
			return 'off';
		}
		if (n === 1) {
			return 'on';
		}
		return 'other';
	}

	/**
	 * NetMode: 0 = no service, 1 = 2G, 2 = 4G.
	 */
	diagnosticsNetModeTag(value: unknown): { label: string; severity: 'success' | 'danger' } | null {
		const n = this.diagnosticsCoerceInt(value);
		if (n === 0) {
			return { label: 'NO SERVICE', severity: 'danger' };
		}
		if (n === 1) {
			return { label: '2G', severity: 'success' };
		}
		if (n === 2) {
			return { label: '4G', severity: 'success' };
		}
		return null;
	}

	hasValidDiagnosticsValueJson(row: MtmaDiagnosticsLog): boolean {
		return this.parseDiagnosticsValueJson(row?.Value) !== null;
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-20rem');
	}

	col3ResponsiveStd(): string {
		return StandardPageLayout.col3ResponsiveStd(this.screenWidth);
	}

	col4Responsive(): string {
		return StandardPageLayout.col4ResponsiveStd(this.screenWidth);
	}

	col3Responsive(): string {
		return (this.screenWidth >= StandardDesignParameters.MIN_WIDTH_MX7_LATER) ? "col-3 px-5" : (this.screenWidth >= StandardDesignParameters.MIN_WIDTH_COL6) ? 'col-3' : 'col-6';
	}

	disableCol(): boolean {
		return this.screenWidth < StandardDesignParameters.MIN_WIDTH_MX7_LATER;
	}

	col6Responsive(): string {
		return StandardPageLayout.col6ResponsiveNoPadding(this.screenWidth);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicAutocomplete(): string {
		return StandardDynamicComponent.dynamicAutocomplete(this.screenWidth);
	}
}
